import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useLingui } from '@lingui/react';
import { t } from '@lingui/core/macro';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../utils/api';
import { authFetch } from '../utils/authFetch';
import { auth } from '../firebase';
import { getProjectRoleLabel, Project } from '../contexts/ProjectContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import ProjectCreationWaitModal from '../components/ProjectCreationWaitModal';
import {
  getProjectCreationSourceValue,
  type CreationSheetSourceValue,
} from '../utils/projectSheetSource';
import './ProjectSelector.css';
import { trackEvent, trackPageView } from '../statsig';

interface ProjectSelectorProps {
  onProjectSelect: (project: Project) => void;
}

interface RenameDialogState {
  projectId: string;
  currentName: string;
  draftName: string;
}

type CreationSourceOption =
  | {
      value: 'system-templates';
      label: string;
      group: string;
      kind: 'system-templates';
    }
  | {
      value: CreationSheetSourceValue;
      label: string;
      group: string;
      kind: 'project';
      projectId: string;
    };

const ProjectSelector: React.FC<ProjectSelectorProps> = ({ onProjectSelect }) => {
  const { i18n: linguiI18n } = useLingui();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [newProjectName, setNewProjectName] = useState<string>('');
  const [selectedCreationSource, setSelectedCreationSource] = useState<CreationSheetSourceValue | null>(null);
  const [creatingProject, setCreatingProject] = useState<boolean>(false);
  const [removingProjectIds, setRemovingProjectIds] = useState<string[]>([]);
  const [renamingProjectId, setRenamingProjectId] = useState<string | null>(null);
  const [renameDialog, setRenameDialog] = useState<RenameDialogState | null>(null);

  const handleLogout = async (): Promise<void> => {
    await signOut(auth);
  };

  const creationSourceOptions = useMemo<CreationSourceOption[]>(() => [
    {
      value: 'system-templates',
      label: t`System templates...`,
      group: t`Templates`,
      kind: 'system-templates',
    },
    ...projects.map((project) => ({
      value: getProjectCreationSourceValue(project.id),
      label: project.name,
      group: t`User Projects`,
      kind: 'project' as const,
      projectId: project.id,
    })),
  ], [linguiI18n.locale, projects]);

  const selectedCreationOption =
    creationSourceOptions.find((option) => option.kind === 'project' && option.value === selectedCreationSource) ?? null;

  const fetchProjects = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      const response = await authFetch(`${getApiUrl()}/projects`);
      const data = await response.json();

      if (data.status === 'OK') {
        setProjects(data.projects);
      } else {
        setError(data.message || t`Failed to fetch projects`);
      }
    } catch (err) {
      setError(t`Failed to load projects. Please try again.`);
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    trackPageView('project_selector');
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleCreateProject = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!newProjectName.trim() || !selectedCreationSource) return;

    try {
      setCreatingProject(true);
      setError('');
      const sourceProjectId = selectedCreationSource.slice('project:'.length);
      const requestBody: {
        name: string;
        sourceProjectId?: string;
      } = {
        name: newProjectName.trim(),
        sourceProjectId,
      };
      const response = await authFetch(`${getApiUrl()}/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (data.status === 'OK') {
        trackEvent('project_created', {
          project_id: data.project?.id,
          project_name: data.project?.name,
          source_type: 'project',
          source_project_id: sourceProjectId,
        });
        onProjectSelect(data.project);
      } else {
        setError(data.message || t`Failed to create project`);
      }
    } catch (err) {
      setError(t`Failed to create project. Please try again.`);
      console.error('Error creating project:', err);
    } finally {
      setCreatingProject(false);
    }
  };

  const handleDeleteProject = async (projectId: string): Promise<void> => {
    if (!window.confirm(t`Are you sure you want to delete this project? This action cannot be undone.`)) return;

    // Trigger animation
    setRemovingProjectIds((prev) => [...prev, projectId]);

    setTimeout(async () => {
      try {
        const response = await authFetch(`${getApiUrl()}/projects/${projectId}`, {
          method: 'DELETE',
        });

        const data = await response.json();

        if (data.status === 'OK') {
          trackEvent('project_deleted', {
            project_id: projectId,
          });
          setProjects((prev) => prev.filter((p) => p.id !== projectId));
          setSelectedCreationSource((prev) =>
            prev === getProjectCreationSourceValue(projectId)
              ? null
              : prev
          );
        } else {
          setError(data.message || t`Failed to delete project`);
        }
      } catch (err) {
        setError(t`Failed to delete project. Please try again.`);
        console.error('Error deleting project:', err);
      } finally {
        setRemovingProjectIds((prev) => prev.filter((id) => id !== projectId));
      }
    }, 300); // match CSS transition duration
  };

  const handleOpenRenameDialog = (project: Project): void => {
    if (!project.sheet_id) {
      window.alert(t`You must first select the project due to technical limitations.`);
      return;
    }

    setRenameDialog({
      projectId: project.id,
      currentName: project.name,
      draftName: project.name,
    });
    setError('');
  };

  const handleCloseRenameDialog = (): void => {
    setRenameDialog(null);
  };

  const handleRenameProjectSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!renameDialog) return;

    const newName: string = renameDialog.draftName.trim();
    if (!newName) return;
    if (newName === renameDialog.currentName) {
      handleCloseRenameDialog();
      return;
    }

    try {
      setError('');
      setRenamingProjectId(renameDialog.projectId);

      const response = await authFetch(`${getApiUrl()}/projects/${renameDialog.projectId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: renameDialog.projectId, newName }),
      });

      const data = await response.json();

      if (data.status === 'OK') {
        trackEvent('project_renamed', {
          project_id: renameDialog.projectId,
          previous_name: renameDialog.currentName,
          next_name: newName,
        });
        setProjects((prev) =>
          prev.map((project) =>
            project.id === renameDialog.projectId ? { ...project, name: newName } : project
          )
        );
        handleCloseRenameDialog();
      } else {
        setError(data.message || t`Failed to rename project`);
      }
    } catch (err) {
      setError(t`Failed to rename project. Please try again.`);
      console.error('Error renaming project:', err);
    } finally {
      setRenamingProjectId(null);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const locale = linguiI18n.locale === 'pl' ? 'pl-PL' : 'en-US';
    return date.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="project-selector">
        <div className="project-selector-language">
          <LanguageSwitcher />
          <button
            type="button"
            className="language-menu-button"
            onClick={handleLogout}
            aria-label={t`Logout`}
            title={t`Logout`}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              logout
            </span>
          </button>
        </div>
        <div className="project-selector-container">
          <h1>{t`Loading projects...`}</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="project-selector">
      <div className="project-selector-language">
        <LanguageSwitcher />
        <button
          type="button"
          className="language-menu-button"
          onClick={handleLogout}
          aria-label={t`Logout`}
          title={t`Logout`}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            logout
          </span>
        </button>
      </div>
      <div className="project-selector-container">
        <h1>{t`Welcome Back`}</h1>
        <p className="subtitle">{t`Your creative space awaits - pick a project or start fresh`}</p>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleCreateProject} className="create-project-form">
          <input
            type="text"
            placeholder={t`New project name`}
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            disabled={creatingProject}
            className="project-name-input"
          />
          <Autocomplete
            value={selectedCreationOption}
            onChange={(_, option) => {
              if (option?.kind === 'system-templates') {
                navigate('/app/system-templates', { state: { projectName: newProjectName } });
                return;
              }
              if (option?.kind === 'project') {
                setSelectedCreationSource(option.value);
              }
            }}
            disabled={creatingProject}
            disablePortal
            options={creationSourceOptions}
            groupBy={(option) => option.group}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, optionValue) => option.value === optionValue.value}
            className="template-select"
            slotProps={{
              popper: {
                className: 'template-select-popper',
              },
            }}
            renderGroup={(params) => (
              <li key={params.key}>
                <div className="template-select-group-header">{params.group}</div>
                <ul className="template-select-group-options">{params.children}</ul>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t`Template or project`}
                size="small"
                variant="outlined"
              />
            )}
          />
          <button
            type="submit"
            disabled={creatingProject || !newProjectName.trim() || !selectedCreationSource}
            className="create-button"
          >
            {creatingProject ? t`Creating...` : t`Create Project`}
          </button>
        </form>

        <div className="projects-list">
          {projects.length === 0 ? (
            <div className="no-projects">
              <p>{t`You don't have any projects yet.`}</p>
              <p>{t`Create one to get started!`}</p>
            </div>
          ) : (
            projects.map((project) => {
              const isRenaming: boolean = renamingProjectId === project.id;
              return (
                <div
                  key={project.id}
                  className={`project-card ${removingProjectIds.includes(project.id) ? 'removing' : ''}`}
                >
                  <div className="project-info">
                    <h3>{project.name}</h3>
                    <p className="project-date">{t`Created`}: {formatDate(project.created_at)}</p>
                    <p className="project-role">{t`Role`}: {getProjectRoleLabel(project.permission_tier)}</p>
                    <p className="project-users">{t`Users`}: {project.allowed_users.join(', ')}</p>
                  </div>
                  <div className="project-actions">
                    <button
                      onClick={() => {
                        trackEvent('project_selected', {
                          project_id: project.id,
                          project_name: project.name,
                        });
                        onProjectSelect(project);
                      }}
                      className="select-button"
                    >
                      {t`Select`}
                    </button>
                    {project.permission_tier === 1 && (
                      <>
                        <button
                          onClick={() => handleOpenRenameDialog(project)}
                          className="rename-button"
                          disabled={isRenaming}
                        >
                          {isRenaming ? t`Renaming...` : t`Rename`}
                        </button>
                        <button onClick={() => handleDeleteProject(project.id)} className="delete-button">{t`Delete`}</button>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {renameDialog && (
        <div className="rename-modal-overlay" onClick={handleCloseRenameDialog}>
          <div className="rename-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{t`Rename Project`}</h2>
            <form onSubmit={handleRenameProjectSubmit}>
              <input
                type="text"
                value={renameDialog.draftName}
                onChange={(e) =>
                  setRenameDialog((prev) =>
                    prev ? { ...prev, draftName: e.target.value } : prev
                  )
                }
                className="project-name-input"
                autoFocus
              />
              <div className="rename-modal-actions">
                <button type="button" className="rename-cancel-button" onClick={handleCloseRenameDialog}>
                  {t`Cancel`}
                </button>
                <button
                  type="submit"
                  className="rename-save-button"
                  disabled={
                    renamingProjectId === renameDialog.projectId ||
                    !renameDialog.draftName.trim() ||
                    renameDialog.draftName.trim() === renameDialog.currentName
                  }
                >
                  {renamingProjectId === renameDialog.projectId ? t`Saving...` : t`Save`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ProjectCreationWaitModal open={creatingProject} />
    </div>
  );
};

export default ProjectSelector;
